import type { FlowChartViewport } from '../../flow-chart/FlowChart';
import type {
  NetworkGraphLayoutSize,
  NetworkGraphPosition,
} from './computeRadialLayout';

export interface FitViewportContent extends NetworkGraphPosition {
  // Half-extent from the point that must stay in view (node outer radius).
  radius: number;
}

export interface FitViewportBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Breathing room beyond the tight content bounds (node rims + label boxes).
export const FIT_VIEWPORT_PADDING = 8;

const DEFAULT_VIEWPORT: FlowChartViewport = { scale: 1, x: 0, y: 0 };

// Viewport transform that shows every given content at once: scale down
// (never up) until the padded content bounds fit the canvas, then place
// `center` (the primary node) at the canvas center — so the landing view
// already matches centerOnSelectedNode and clicking it moves nothing.
// Bounds union node centers expanded by radius with axis-aligned label boxes
// — padding alone cannot cover ray labels (radius+gap+textWidth/2 ≫ rim).
export interface FitViewportOptions {
  center: NetworkGraphPosition;
  boxes: FitViewportBox[];
}

export const computeFitViewport = (
  contents: FitViewportContent[],
  size: NetworkGraphLayoutSize,
  { center, boxes }: FitViewportOptions
): FlowChartViewport => {
  if (contents.length === 0 && boxes.length === 0) {
    return DEFAULT_VIEWPORT;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { x, y, radius = 0 } of contents) {
    minX = Math.min(minX, x - radius);
    minY = Math.min(minY, y - radius);
    maxX = Math.max(maxX, x + radius);
    maxY = Math.max(maxY, y + radius);
  }
  for (const { x, y, width, height } of boxes) {
    minX = Math.min(minX, x - width / 2);
    minY = Math.min(minY, y - height / 2);
    maxX = Math.max(maxX, x + width / 2);
    maxY = Math.max(maxY, y + height / 2);
  }

  const paddedWidth = maxX - minX + FIT_VIEWPORT_PADDING * 2;
  const paddedHeight = maxY - minY + FIT_VIEWPORT_PADDING * 2;
  const scale = Math.min(
    1,
    size.width / paddedWidth,
    size.height / paddedHeight
  );

  return {
    scale,
    x: size.width / 2 - scale * center.x,
    y: size.height / 2 - scale * center.y,
  };
};
